import type { Socket } from "socket.io-client"

import { generateId } from "./id"
import { useUnsentMessages } from "./unsent-messages"

export const LIVE_CHAT_ACK_TIMEOUT_MS = 15_000
export const LIVE_CHAT_ROW_WAIT_MS = 10_000

export interface LiveChatReplyPayload {
	sessionId: string
	content: string
	imageUrl?: string | null
	name?: string
	attachments: string
	cc?: string[]
	bcc?: string[]
}

/**
 * Sends a teammate's live chat reply and keeps it in the unsent store until the
 * server confirms it, so a slow or dropped connection never makes it vanish.
 */
export function sendLiveChatReply(
	socket: Socket,
	payload: LiveChatReplyPayload,
	threadId: string,
	id: string = generateId(),
	onSent?: (messageId: string) => void,
): void {
	const unsent = useUnsentMessages.getState()
	unsent.track({ id, threadId, channel: "live-chat", html: payload.content })

	const retry = () => {
		useUnsentMessages.getState().settle(id)
		sendLiveChatReply(socket, payload, threadId, generateId(), onSent)
	}

	let answered = false
	const timeout = setTimeout(() => {
		if (!answered) useUnsentMessages.getState().fail(id, retry)
	}, LIVE_CHAT_ACK_TIMEOUT_MS)

	socket.emit(
		"live-chat:send-message",
		payload,
		(res?: { ok?: boolean; messageId?: string }) => {
			answered = true
			clearTimeout(timeout)
			if (res?.ok === false) {
				useUnsentMessages.getState().fail(id, retry)
				return
			}
			if (res?.messageId) {
				// Keep the row on screen until the saved message replaces it.
				useUnsentMessages.getState().confirm(id, res.messageId)
				setTimeout(
					() => useUnsentMessages.getState().settle(id),
					LIVE_CHAT_ROW_WAIT_MS,
				)
				onSent?.(res.messageId)
			} else {
				useUnsentMessages.getState().settle(id)
			}
		},
	)
}
